import { DropdownMenu } from 'radix-ui'
import { useNavigate } from 'react-router'
import { ROLE_LABELS } from '../auth/require-auth.js'
import { useCurrentUser } from '../auth/session-context.js'
import { IconChevronDown } from '../components/icon.js'
import { cn } from '../lib/cn.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

/**
 * Переключатель воркспейсов.
 *
 * При единственном воркспейсе это не список, а просто название: выпадающее
 * меню с одним пунктом — обещание выбора, которого нет, и человек тратит
 * нажатие, чтобы это выяснить.
 *
 * Переключение — это переход по адресу, а не изменение состояния. Поэтому
 * оно попадает в историю браузера: «назад» возвращает в прежний воркспейс,
 * как и ожидается от перехода между разделами.
 */
export function WorkspaceSwitcher() {
  const user = useCurrentUser()
  const workspace = useCurrentWorkspace()
  const navigate = useNavigate()

  if (user.workspaces.length <= 1) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-line bg-raised px-2.5 py-1.5">
        <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-accent" />
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink">
          {workspace.name}
        </span>
        <span className="shrink-0 text-[11px] text-muted">{ROLE_LABELS[workspace.role]}</span>
      </div>
    )
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-md border border-line',
          'bg-raised px-2.5 py-1.5 text-left hover:bg-paper',
        )}
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-ink">{workspace.name}</span>
          <span className="block text-xs text-muted">{ROLE_LABELS[workspace.role]}</span>
        </span>
        <IconChevronDown size={13} className="shrink-0 text-muted" />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="z-50 min-w-56 rounded-md border border-line bg-raised p-1 shadow-lg"
        >
          <DropdownMenu.Label className="px-2 py-1.5 text-xs tracking-wide text-muted uppercase">
            Воркспейсы
          </DropdownMenu.Label>

          {user.workspaces.map((item) => (
            <DropdownMenu.Item
              key={item.id}
              onSelect={() => void navigate(`/w/${item.id}`)}
              className={cn(
                'flex cursor-pointer items-center justify-between gap-3 rounded px-2 py-1.5 text-sm',
                'outline-none data-highlighted:bg-accent-soft data-highlighted:text-accent',
              )}
            >
              <span className="truncate">{item.name}</span>
              <span className="text-xs text-muted">{ROLE_LABELS[item.role]}</span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
